<template>
  <div>
    <template
      v-if="
        pageData?.page?.fields?.visibility && visibilityStatus === 'checking'
      "
    >
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
        "
      >
        <LoaderDots />
      </div>
    </template>

    <template
      v-else-if="
        pageData?.page?.fields?.visibility && visibilityStatus === 'unknown'
      "
    >
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 20px;
        "
      >
        <div style="max-width: 480px; text-align: left">
          <h2 style="margin: 0 0 8px; font-size: 20px">
            We need one quick setting before we can show this page
          </h2>

          <p style="margin: 0 0 16px; line-height: 1.5">
            This page is restricted by region. We can’t confirm access until
            your country/region preference is set.
          </p>

          <div
            style="
              display: inline-flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            "
          >
            <BaseButton type="button" @click="onRequestLocaleSetter">
              Set my region
            </BaseButton>
          </div>

          <p style="margin: 14px 0 0; font-size: 13px; opacity: 0.8">
            If you use a browser setting that blocks cookies, you may need to
            allow site preferences for this page to load correctly.
          </p>
        </div>
      </div>
    </template>

    <BlueprintResolver
      v-else-if="
        !pageData?.page?.fields?.visibility || visibilityStatus === 'visible'
      "
      :data="pageData?.blueprint"
    />
    <ContentJourneyFlyout
      v-if="
        isHydrated &&
        pageData?.page &&
        (!pageData.page.fields?.visibility || visibilityStatus === 'visible')
      "
      :journeys="contentJourneys"
      :loading="isContentJourneyLoading"
      :active-journey-id="activeJourneyId"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { arrayAsString } from '@/utils'
import { DASHBOARD_ENTRY_ID } from '~/utils/const'
import { buildRedirectUrl } from '~/utils/parsePageLevel'
import { requireContentfulEntry } from '~/utils/requireContentfulEntry'
import { resolveUnauthorizedPath } from '~/utils/visibilityRedirect'
import { useContentJourney } from '~/composables/useContentJourney'
import { useSiteNotificationStore } from '~/store/siteNotificationStore'
import { getSiteNotificationReferences } from '~/utils/siteNotification'

import type {
  TypePageBuilderSkeleton,
  TypePageBuilderWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const slug = route.params?.slug ? arrayAsString(route.params?.slug) : '/'
const activeJourneyId = computed(() => arrayAsString(route.query.journey) || '')
const isHydrated = ref(false)

const siteNotificationStore = useSiteNotificationStore()
const registeredRoutePath = ref<string | null>(null)
const pageRegistrationId = ref<number | undefined>(undefined)

const registerPageNotificationReferences = () => {
  const nextRoutePath = route.path
  const routeChanged =
    registeredRoutePath.value !== null &&
    registeredRoutePath.value !== nextRoutePath

  if (routeChanged) {
    siteNotificationStore.clearPageCandidates(
      registeredRoutePath.value as string,
      pageRegistrationId.value,
    )
  }

  registeredRoutePath.value = nextRoutePath
  // On a path change register an empty source immediately. The old async
  // payload must not remain eligible while the new page resolves.
  const page = routeChanged ? null : pageData.value?.page
  pageRegistrationId.value = siteNotificationStore.registerPageCandidates(
    nextRoutePath,
    page ? [...getSiteNotificationReferences(page)] : [],
  )
}

// Register ownership before the async page request begins. This gives the
// layout a current-route readiness signal and prevents global candidates from
// being evaluated against an unresolved page source during navigation.
const registerEmptyPageNotificationReferences = () => {
  registeredRoutePath.value = route.path
  pageRegistrationId.value = siteNotificationStore.registerPageCandidates(
    route.path,
    [],
  )
}

registerEmptyPageNotificationReferences()

onMounted(() => {
  isHydrated.value = true
})

const { data: pageData, error } = await useAsyncData(
  `page-data-${route.params?.slug}-${locale.value}`,
  async () => {
    // START CONTENTFUL DATA
    const { $client } = useNuxtApp()

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypePageBuilderSkeleton>(
        {
          content_type: 'pageBuilder',
          'fields.path[in]': [slug || '/'],
          limit: 1,
          include: 10,
          locale: locale.value,
        },
      )
    const pageData = requireContentfulEntry(response?.items?.[0])
    // END CONTENTFUL DATA

    // START USER DATA
    const userStore = useUserStore()
    const { loggedIn, highestUserLevel, stores } = storeToRefs(userStore)
    // END USER DATA

    // START BLUEPRINT DATA
    let blueprintToDisplay = pageData?.fields?.blueprint
    const defaultBlueprint = pageData?.fields?.blueprint
    const blueprintLevels: (
      | TypePageBuilderWithoutUnresolvableLinksResponse['fields']['level1']
      | TypePageBuilderWithoutUnresolvableLinksResponse['fields']['level2']
      | TypePageBuilderWithoutUnresolvableLinksResponse['fields']['level3']
      | TypePageBuilderWithoutUnresolvableLinksResponse['fields']['level4']
    )[] = [
      pageData?.fields?.level1,
      pageData?.fields?.level2,
      pageData?.fields?.level3,
      pageData?.fields?.level4,
    ]

    // Check if there is level-gated content for the page
    const hasBlueprintLevels = blueprintLevels.some(
      (level) => level !== undefined,
    )
    // END BLUEPRINT DATA

    const accessDenialRedirectUrl =
      pageData?.fields?.accessDenialRedirectUrl || '/'

    const redirectUrl = buildRedirectUrl(locale.value, accessDenialRedirectUrl)

    // If there is no blueprint matching user level or lower, redirect user
    if (hasBlueprintLevels && loggedIn) {
      for (
        let currentLevel = highestUserLevel.value;
        currentLevel >= 1;
        currentLevel--
      ) {
        if (blueprintLevels[currentLevel - 1]) {
          blueprintToDisplay = blueprintLevels[currentLevel - 1]
          break
        }
      }
    }

    if (!blueprintToDisplay && !defaultBlueprint) {
      if (redirectUrl) {
        navigateTo(redirectUrl)
      } else {
        throw createError({
          statusCode: 404,
          statusMessage: 'Page Not Found',
          fatal: true,
        })
      }
    }

    // if there is a blueprint matching user level or lower, return blueprint
    return {
      blueprint: blueprintToDisplay,
      page: pageData,
    }
  },
  {
    // transform(input) {
    //   return {
    //     page: input.page,
    //     blueprint: input.blueprint,
    //     fetchedAt: useUnixTimestamp(),
    //   }
    // },
    // getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

watch(
  () => [route.path, pageData.value?.page] as const,
  registerPageNotificationReferences,
  { immediate: true },
)

onBeforeUnmount(() => {
  if (!registeredRoutePath.value) return
  siteNotificationStore.clearPageCandidates(
    registeredRoutePath.value,
    pageRegistrationId.value,
  )
})

if (error.value) throw error.value

watch(error, (asyncDataError) => {
  if (asyncDataError) showError(asyncDataError)
})

if (pageData.value?.page) {
  getPageMetaData(pageData.value.page)
}

const { visibilityStatus } = usePageVisibility({
  visibility: () => pageData.value?.page?.fields?.visibility,
  onDenied: async (decision) => {
    const unauthorizedPath = resolveUnauthorizedPath(decision)
    if (unauthorizedPath) {
      await navigateTo({
        path: localePath(unauthorizedPath),
        query:
          unauthorizedPath === '/unauthorized'
            ? { returnTo: route.fullPath }
            : undefined,
      })
      return
    }

    const redirectUrl = buildRedirectUrl(
      locale.value,
      pageData.value?.page?.fields?.accessDenialRedirectUrl || '/',
    )

    return navigateTo(redirectUrl)
  },
})

const { journeys: contentJourneys, isLoading: isContentJourneyLoading } =
  useContentJourney(() => pageData.value?.page)

const onRequestLocaleSetter = () => {
  const localeModalStore = useLocalModalStore()
  localeModalStore.showLocaleModal = true
}

const globalStore = useGlobalStore()
watch(
  () => pageData.value,
  (newVal) => {
    globalStore.pageEntrySys = newVal?.page?.sys
  },
  { immediate: true },
)
</script>
